// Types
import { TraceInfo } from '../../../types/TraceInfo';

// Hooks
import useDateTimeFormat from '../../../hooks/useDateTimeFormat';

// Components
import { OverviewCard, OverviewGrid, GridField } from '../../common/OverviewCard/OverviewCard';

interface EquipmentTraceStageProp {
  traceInfo: TraceInfo;
}

const val = <T,>(v: T | undefined | null): T | string => (v == null || v === '' ? '-' : v);

export default function EquipmentTraceStage(props: EquipmentTraceStageProp) {
  const { traceInfo } = props;

  const { toStandardDate, withTimeMode } = useDateTimeFormat();

  const formatDate = (value?: string | Date | null): string => {
    if (!value) return '-';

    const formatted = toStandardDate(value);

    return formatted ? String(formatted) : '-';
  };

  const equipment = traceInfo.equipment;
  const sighting = traceInfo.latestSighting;
  const pairing = traceInfo.currentPairing;

  //  Equipment fields (always shown)
  const equipmentFields: GridField[] = [
    { label: 'Type', value: val(equipment?.typeDescr) },
    { label: 'Status', value: val(equipment?.statusDescr) },
    { label: 'Condition', value: val(equipment?.condition) },
    { label: 'ULD Group', value: val(equipment?.uldGroupIataCode) },
    { label: 'ULD Type', value: val(equipment?.uldTypeIataCode) },
    { label: 'Eligible for Tagging', value: equipment?.isEligibleForTagging ? 'Yes' : 'No' },
    { label: 'Part Number', value: val(equipment?.partNumber) },
    { label: withTimeMode('Log Date'), value: formatDate(equipment?.logDateTime) },
    { label: withTimeMode('Message Date'), value: formatDate(equipment?.msgDateTime) },
    { label: 'Carrier', value: val(sighting?.networkCarrierCode) },
  ];

  //  Sighting fields (only if present, labels prefixed to disambiguate)
  const sightingFields: GridField[] = sighting
    ? [
        { label: 'Sighting Station', value: val(sighting.stationIataCode) },
        { label: 'Sighting Facility', value: val(sighting.facilityName) },
        { label: 'IMEI', value: val(sighting.imei) },
        { label: 'Reader', value: val(sighting.readerName) },
        { label: withTimeMode('Last Seen'), value: formatDate(sighting.lastSeenDateTime) },
      ]
    : [];

  //  Pairing fields (only if present)
  const pairingFields: GridField[] = pairing
    ? [
        { label: 'Paired Device', value: val(pairing.deviceId) },
        { label: withTimeMode('Paired Date'), value: formatDate(pairing.pairedDateTime) },
        { label: 'Paired By', value: val(pairing.pairedUserName) },
        { label: 'Pairing Station', value: val(pairing.stationIataCode) },
        { label: 'Pairing Facility', value: val(pairing.facilityName) },
        { label: 'Pairing Method', value: val(pairing.pairingMethodDescr) },
        { label: 'Pairing Status', value: val(pairing.equipmentPairingStatusDescr) },
      ]
    : [];

  //  Chunk all fields into rows of 4
  const allFields = [...equipmentFields, ...sightingFields, ...pairingFields];
  const rows: GridField[][] = [];
  for (let i = 0; i < allFields.length; i += 4) {
    rows.push(allFields.slice(i, i + 4));
  }

  return (
    <OverviewCard
      title='Equipment Details'
      headerRight={
        <>
          <span style={{ color: '#6b7280' }}>Serial Number:</span>
          <strong style={{ color: '#1f2229' }}>{val(equipment?.serialNumber)}</strong>
        </>
      }
    >
      <OverviewGrid columns={4} rows={rows} />
    </OverviewCard>
  );
}
